import type { ImageCandidates } from "@provider/image-url";
import { isImageCandidates } from "@provider/image-url";
import type { Sx } from "dripsy";
import { useSx } from "dripsy";
import type { ImageContentFit } from "expo-image";
import * as React from "react";
import type { CSSProperties, SyntheticEvent } from "react";
import { StyleSheet } from "react-native";

// Stappen bij kandidaten: eerst <picture> met AVIF en de JPEG-variant, dan
// alleen de JPEG-variant, dan het origineel, en als ook dat mislukt een leeg
// vlak. Zo blijft alles werken zolang de varianten nog niet bestaan.
type Step = "picture" | "jpeg" | "original" | "empty";

// nextStep bepaalt na een fout de volgende stap. In <picture> kiest de browser
// zelf; currentSrc zegt welke het werd. Was dat al de JPEG (geen AVIF-support),
// dan heeft de JPEG-variant opnieuw proberen geen zin.
function nextStep(step: Step, candidates: ImageCandidates, currentSrc: string): Step {
  switch (step) {
    case "picture":
      return currentSrc === candidates.jpeg ? "original" : "jpeg";
    case "jpeg":
      return "original";
    default:
      return "empty";
  }
}

// plainUri haalt de URL uit een gewone bron ({ uri } of een geïmporteerd asset).
function plainUri(source: unknown): string {
  return (source as { uri?: string })?.uri || (source as string);
}

export default function CustomImage({
  source,
  contentFit,
  sx,
  alt,
  lazy = true,
  priority = false,
}: {
  source: { uri: string } | ImageCandidates;
  contentFit?: ImageContentFit;
  sx?: Sx;
  alt: string;
  lazy?: boolean;
  // priority: voor de LCP-foto's bovenaan (niet lazy, met voorrang ophalen).
  priority?: boolean;
}) {
  const generateSx = useSx();
  const candidates = isImageCandidates(source) ? source : undefined;
  const sourceKey = candidates ? candidates.original : plainUri(source);

  // De stap hoort bij een bron: komt er een andere foto in dit component
  // (lijsten hergebruiken componenten), dan begint hij weer vooraan.
  const [state, setState] = React.useState<{ key: string; step: Step }>({ key: sourceKey, step: "picture" });
  const step: Step = state.key === sourceKey ? state.step : "picture";

  // Zonder contentFit rekt een <img> het beeld uit tot zijn vak; native
  // (expo-image) is de default "cover". Hier dezelfde default, zodat web en
  // native gelijk ogen. Productfoto's geven zelf "contain" mee (nooit
  // bijsnijden of uitrekken in een vierkant vak).
  const style = StyleSheet.flatten(
    generateSx({
      ...sx,
      objectFit: contentFit ?? "cover",
    } as any)
  ) as CSSProperties;

  const onError = (e: SyntheticEvent<HTMLImageElement>) => {
    const current = e.currentTarget.currentSrc;
    setState({ key: sourceKey, step: candidates ? nextStep(step, candidates, current) : "empty" });
  };

  if (step === "empty") {
    return <div style={style} />;
  }

  const eager = priority || !lazy;
  const imgProps = {
    style,
    onError,
    alt,
    width: style.width as number | string | undefined,
    height: style.height as number | string | undefined,
    decoding: priority ? ("sync" as const) : eager ? undefined : ("async" as const),
    loading: eager ? ("eager" as const) : ("lazy" as const),
    fetchPriority: priority ? ("high" as const) : undefined,
  };

  if (!candidates) {
    return <img src={plainUri(source)} {...imgProps} />;
  }
  if (step === "picture") {
    // display: contents, zodat <picture> de opmaak van de <img> niet raakt.
    return (
      <picture style={{ display: "contents" }}>
        <source type="image/avif" srcSet={candidates.avif} />
        <img src={candidates.jpeg} {...imgProps} />
      </picture>
    );
  }
  return <img src={step === "jpeg" ? candidates.jpeg : candidates.original} {...imgProps} />;
}
